/**
 * DocumentTitle — Updates document.title based on current hash route.
 *
 * Mounted once at the App.tsx top level. Listens to hashchange + popstate
 * and maps the path segment to a human-readable title. Falls back to the
 * default index.html title.
 */
import { useEffect } from "react";

const BASE = "Attilius";

const ROUTE_TITLES: Array<[RegExp, string]> = [
  [/^\/?$/,                            "Home"],
  [/^\/login\/?$/,                     "Sign in"],
  [/^\/forgot-password\/?$/,           "Reset password"],
  [/^\/dashboards?/,                   "Dashboards"],
  [/^\/accounts\/[^/]+/,               "Account detail"],
  [/^\/accounts/,                      "Accounts"],
  [/^\/pipeline/,                      "Pipeline"],
  [/^\/templates/,                     "Templates"],
  // W66.D: /library route deleted — title entry removed.
  [/^\/today/,                         "Today"],
  [/^\/todays-brief/,                  "Today's brief"],
  [/^\/marketing\/plan/,               "Marketing plan"],
  [/^\/coaching/,                      "Coaching"],
  [/^\/audit-review/,                   "Account audit"],
  [/^\/audit/,                           "Account audit"], // legacy alias — redirects to /audit-review
  [/^\/settings\/account/,             "Account settings"],
  [/^\/settings\/company/,             "Company settings"],
  [/^\/settings\/email/,               "Email settings"],
  [/^\/settings\/override-policy/,     "Override policy"],
  [/^\/settings\/integrations/,        "Integrations"],
  [/^\/rapid-intake/,                   "Rapid intake"],
  [/^\/opportunity-dna/,                 "Opportunity DNA"],
  [/^\/contacts\/holding/,               "Holding queue"],
  [/^\/cadence\/bdr/,                    "Cadence - BDR"],
  [/^\/dashboards-preview/,              "Dashboards preview"],
  // W66.D: /wall-poster route deleted — title entry removed.
  [/^\/admin\/audit/,                    "Audit log"],
  [/^\/admin\/support/,                "Support queue"],
  [/^\/admin\/pricing/,                "Pricing editor"],
  [/^\/admin/,                         "Admin"],
  [/^\/legal\/terms/,                  "Terms of Service"],
  [/^\/legal\/privacy/,                "Privacy Policy"],
  [/^\/set-password/,                  "Set password"],
];

function titleForHash(): string {
  const raw = window.location.hash.replace(/^#/, "") || "/";
  const path = raw.split("?")[0];
  for (const [re, label] of ROUTE_TITLES) {
    if (re.test(path)) return `${label} · ${BASE}`;
  }
  return `${BASE} — Intelligence Platform`;
}

export default function DocumentTitle() {
  useEffect(() => {
    function apply() { document.title = titleForHash(); }
    apply();
    window.addEventListener("hashchange", apply);
    window.addEventListener("popstate", apply);
    return () => {
      window.removeEventListener("hashchange", apply);
      window.removeEventListener("popstate", apply);
    };
  }, []);
  return null;
}
